import { Alert, Button, Group, Modal, ScrollArea, Stack, Table, Text } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import type { PreviewView } from '@parts/shared';
import { useI18n } from '../../i18n';
import { IconAlert, IconCircleCheck, IconInfo } from '../../icons';
import classes from './PreviewModal.module.css';

interface Props {
  view: PreviewView | null;
  committing: boolean;
  canCommit: boolean;
  onClose: () => void;
  onCommit: () => void;
  /** Надпись на кнопке подтверждения (по умолчанию «Провести»). */
  commitLabel?: string;
}

/** Предпросмотр проведения: что будет записано и что изменится. */
export function PreviewModal({ view, committing, canCommit, onClose, onCommit, commitLabel }: Props) {
  const mobile = useMediaQuery('(max-width: 700px)');
  const { t, tx } = useI18n();
  return (
    <Modal
      opened={!!view}
      onClose={onClose}
      title={<span className={classes.title}>{tx(view?.title)}</span>}
      size={1040}
      fullScreen={mobile}
      closeOnClickOutside={!committing}
      closeOnEscape={!committing}
    >
      {view && (
        <Stack gap="md">
          <dl className={classes.meta}>
            {view.meta.map(([k, v]) => (
              <div key={k} className={classes.metaRow}>
                <dt>{tx(k)}</dt>
                <dd>{tx(v)}</dd>
              </div>
            ))}
          </dl>

          <div className={classes.cards}>
            {view.cards.map((c) => (
              <div key={c.label} className={classes.card}>
                <div className={classes.cardValue}>{c.value}</div>
                <div className={classes.cardLabel}>{tx(c.label)}</div>
              </div>
            ))}
          </div>

          <Alert variant="light" color="accent" py="xs" icon={<IconInfo size={16} />}>
            {tx(view.notice)}
          </Alert>

          {view.warnings.length > 0 && (
            <Alert variant="light" color="yellow" title={t('Обратите внимание')} py="xs" icon={<IconAlert size={16} />}>
              <ul className={classes.warnings}>
                {view.warnings.map((w) => (
                  <li key={w}>{tx(w)}</li>
                ))}
              </ul>
            </Alert>
          )}

          {view.groups.map((g) => (
            <div key={g.title}>
              <Text fw={600} mb={6} className={classes.groupTitle} data-tone={g.tone}>
                {tx(g.title)}
              </Text>
              <ScrollArea.Autosize mah={360} type="auto" className={classes.tableWrap}>
                <Table stickyHeader highlightOnHover className={classes.table}>
                  <Table.Thead>
                    <Table.Tr>
                      {g.columns.map((c, i) => (
                        <Table.Th key={c} ta={g.numeric.includes(i) ? 'right' : 'left'}>
                          {tx(c)}
                        </Table.Th>
                      ))}
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {g.rows.map((row, r) => (
                      <Table.Tr key={r}>
                        {row.map((cell, i) => (
                          <Table.Td key={i} ta={g.numeric.includes(i) ? 'right' : 'left'} className={cell.includes('→') ? classes.changed : ''}>
                            {tx(cell)}
                          </Table.Td>
                        ))}
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </ScrollArea.Autosize>
            </div>
          ))}

          <Group justify="flex-end" gap="sm" className={classes.footer}>
            {!canCommit && (
              <Text size="sm" c="red">
                {t('У текущей роли нет прав на проведение.')}
              </Text>
            )}
            <Button variant="default" onClick={onClose} disabled={committing}>
              {t('Отмена')}
            </Button>
            <Button
              color="go"
              className={classes.commit}
              leftSection={<IconCircleCheck size={17} />}
              onClick={onCommit}
              loading={committing}
              disabled={!canCommit}
            >
              {commitLabel ?? t('Провести')}
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
